import { useLayoutEffect, useRef } from 'react';
import { Link } from 'wouter';
import { ArrowUpRight } from 'lucide-react';
import { useStore } from '@/components/StoreContext';
import type { StoreProduct } from '@/lib/store';
import { getBrandDisplayName } from '@/lib/brands';
import { formatBDT } from '@/lib/pricing';
import { getProductPriceSummary } from '@/lib/estimated-pricing';
import { getDeliveryStatusText } from '@/components/DeliveryNote';
import { PRODUCT_IMAGE_WIDTHS, responsiveShopifyImageSources } from '@/lib/responsive-images';

function ProductBrandBadge({ label }: { label: string }) {
  const badgeRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const badge = badgeRef.current;
    const media = badge?.parentElement;
    if (!badge || !media) return;

    const fitText = () => {
      const baseSize = 9;
      const minSize = 5.5;
      const step = 0.25;
      let size = baseSize;

      badge.style.fontSize = `${baseSize}px`;
      while (size > minSize && badge.scrollWidth > badge.clientWidth) {
        size = Math.max(minSize, size - step);
        badge.style.fontSize = `${size}px`;
      }
    };

    fitText();
    const resizeObserver = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(fitText);
    resizeObserver?.observe(media);
    void document.fonts?.ready.then(() => {
      if (badge.isConnected) fitText();
    });

    return () => resizeObserver?.disconnect();
  }, [label]);

  return <span
    ref={badgeRef}
    className="product-card__brand-badge absolute left-3 top-3 bg-[#f7f5ef]/90 px-1.5 py-1 mono text-[9px] tracking-[.12em] text-[#34231d] uppercase"
    title={label}
  >{label}</span>;
}

export function ProductCard({
  product,
  index = 0,
  tone = "default",
  loading,
}: {
  product: StoreProduct;
  index?: number;
  tone?: "default" | "dark";
  loading?: "eager" | "lazy";
}) {
  const { storefrontSettings, settingsReady } = useStore();
  const soldOut = !product.variants.some(variant => variant.available);
  const summary = getProductPriceSummary(product, storefrontSettings);
  const price = summary?.lowestPricing ?? null;
  const isDisplayedOnSale = summary?.isOnSale === true;
  const deliveryStatus = getDeliveryStatusText(product, storefrontSettings);
  const hasCatalogBrand = Boolean(product.brandSlug && product.brandSlug !== "ethnc");
  const productHref = hasCatalogBrand ? `/brands/${product.brandSlug}/products/${product.handle}` : `/products/${product.handle}`;
  const image = product.images[0];
  const imageSources = image ? responsiveShopifyImageSources(image.src, PRODUCT_IMAGE_WIDTHS, 640) : null;
  return <Link href={productHref} className={`product-card group block w-full min-w-0 ${tone === "dark" ? "text-[#f8f5ef]" : ""} ${soldOut ? "opacity-60 grayscale" : ""}`} data-sold-out={soldOut || undefined} data-testid={`link-product-${product.id}`}>
    <div className="product-card__media relative aspect-[.73] overflow-hidden bg-[#e9e4dc]">
      {image
        ? <img src={imageSources?.src ?? image.src} srcSet={imageSources?.srcSet} sizes={imageSources?.srcSet ? "(max-width: 767px) 50vw, (max-width: 1023px) 33vw, 25vw" : undefined} alt={product.title} width={image.width} height={image.height} loading={loading ?? (index > 1 ? 'lazy' : 'eager')} decoding="async" className="product-image h-full w-full object-cover"/>
        : <div className="h-full w-full"/>}
        <ProductBrandBadge label={product.brandSlug ? getBrandDisplayName(product.brandSlug) : product.product_type || "Ethnc"} />
        {settingsReady && isDisplayedOnSale && <span className="product-card__sale-badge absolute right-3 top-3 bg-[#815348] px-2 py-1 mono text-[9px] tracking-[.12em] text-white">{summary!.discountPercent > 0 ? `-${summary!.discountPercent}%` : "SALE"}</span>}
      <span className="absolute bottom-3 right-3 flex h-9 w-9 translate-y-2 items-center justify-center rounded-full bg-[#f7f5ef] opacity-0 transition-all group-hover:translate-y-0 group-hover:opacity-100"><ArrowUpRight size={17}/></span>
    </div>
    <div className="product-card__details">
      <p className="product-card__title" data-testid={`text-product-title-${product.id}`}>{product.title}</p>
      <div className={`product-card__price mono ${tone === "dark" ? "product-card__price--dark" : ""}`} data-testid={`text-product-price-${product.id}`}>
        {settingsReady && price ? <>
          <span className="product-card__price-current">{summary?.pricesDiffer ? "From " : ""}{formatBDT(price.salePriceBDT)}</span>
          {price.isOnSale && price.originalPriceBDT !== null && <div className="product-card__price-comparison">
            <span className="product-card__price-old">{formatBDT(price.originalPriceBDT)}</span>
            {price.discountPercent > 0 && <span className="product-card__price-discount">SAVE {price.discountPercent}%</span>}
          </div>}
          {deliveryStatus && <span className="product-card__delivery">{deliveryStatus}</span>}
        </> : <span className="product-card__price-current">—</span>}
      </div>
      <p className={`product-card__sizes mono ${tone === "dark" ? "product-card__sizes--dark" : ""}`} data-testid={`text-product-size-count-${product.id}`}>{product.variants.filter(v => v.available).length} SIZES AVAILABLE</p>
    </div>
  </Link>;
}
export function ProductSkeletons() {
  return <div className="grid min-w-0 grid-cols-2 gap-x-3 gap-y-8 md:grid-cols-3 md:gap-x-6 lg:grid-cols-4">{Array.from({ length: 8 }, (_, i) => <div key={i} className="min-w-0"><div className="skeleton aspect-[.73] w-full"/><div className="skeleton mt-3 h-4 w-3/4"/><div className="skeleton mt-2 h-3 w-1/3"/></div>)}</div>;
}